import React, { useEffect, useState } from 'react';
import { Calendar, MapPin, Target, X, Maximize2 } from 'lucide-react';
import { useTheme } from '../../ThemeContext.tsx';
import { formatMonthYearShort, getCutoffMonthYear } from '../../utils/reportingPeriod.ts';
import releaseNotesData from '../../data/release-notes.json'

type GuidelineProps = {
    isOpen: boolean;
    onClose: () => void;
    hasUnRead: boolean;
    onMarkRead: () => void;
};

const Guideline: React.FC<GuidelineProps> = ({ isOpen, onClose, hasUnRead, onMarkRead }) => {
    const { colors } = useTheme();
    const [showReleaseNotes, setShowReleaseNotes] = useState(false);
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const cutoffDateLabel = formatMonthYearShort(getCutoffMonthYear());

    // Reset view when opening
    useEffect(() => {
        if (isOpen) {
            setShowReleaseNotes(false);
        }
    }, [isOpen]);

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (isOpen && !target.closest('.guideline-panel') && !target.closest('.image-preview-modal')) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose]);

    const opcos = [
        'Cambodia', 'Indonesia', 'Korea On-trade', 'Korea Off-trade', 'Laos', 'Malaysia', 'Myanmar',
        'New Caledonia', 'New Zealand', 'Papua New Guinea',
        'Philippines', 'Singapore', 'Taiwan', 'Vietnam'
    ];

    const distributionPowerKpis = [
        'Volume from Direct Retail Outlets',
        'Volume from Wholesalers',
        'No. of Active Selling Outlets',
        'No. of Wholesalers',
        'Volume per Active Selling Outlet',
        'Direct Coverage %',
        'Numeric Distribution %',
        'Outlet Universe',
    ];

    const transactionCaptureKpis = [
        'Net Revenue from e-Business',
        'Total Fragmented Trade Revenue',
        'No. of Heineken Sales Reps',
        'No. of Distributor Sales Reps',
        'Active B2B Customers (% of FT)',
        'BTV of FT Revenue %',
        'Outlets per Distributor Sales Reps',
        'Outlets per HEINEKEN Sales Reps',
    ];

    const executionExcellenceKpis = [
        'PICOS Availability Score',
        '% Call Compliance',
        'No. of SKUs per ASO',
    ];
    const customerLoyaltyKpis = [
        '% NPS Score',
        '% NPS Response Rate',
        '% NPS Value of Revenue',
    ]

    return (
        <div className={`fixed inset-0 z-40 pointer-events-none transition-opacity duration-500 ${isOpen ? 'opacity-200' : 'opacity-0'}`}>
            <div
                className={`absolute inset-0 bg-black/20 transition-opacity duration-500 ${isOpen ? 'opacity-200 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
            />
            <div
                className={`guideline-panel fixed right-0 top-0 h-full ${showReleaseNotes && isOpen ? 'lg:w-[600px] md:w-[500px]' : 'lg:w-[400px] md:w-[380px] sm:w-80'} ${colors.bg.secondary} border-l ${colors.border.primary} shadow-xl transform transition-all duration-500 ease-in-out ${isOpen ? 'translate-x-0 pointer-events-auto' : 'translate-x-full pointer-events-none'}`}
            >
                <div className="flex flex-col h-full">
                    {/* Header */}
                    <div className={`flex items-center justify-between p-[14px] border-b ${colors.border.primary}`}>
                        <div className="flex items-center gap-2">
                            <h2 className={`text-lg font-semibold ${colors.text.primary}`}>
                                {showReleaseNotes ? '' : 'Guidelines'}
                            </h2>
                        </div>
                        <button
                            onClick={() => {
                                setShowReleaseNotes(!showReleaseNotes);
                                if (!showReleaseNotes) {
                                    onMarkRead();
                                }
                            }}
                            className={`cursor-pointer relative flex items-center px-3 py-1 rounded-full border transition-all duration-200 ${showReleaseNotes
                                ? 'bg-blue-50 border-blue-200 text-blue-600 shadow-sm'
                                : `${colors.border.primary} ${colors.interactive.secondaryHover}`
                                }`}
                        >
                            <span className="text-sm font-semibold text-[#13670B]">Release Notes</span>
                            {hasUnRead && !showReleaseNotes && (
                                <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5">
                                    <span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75 animate-ping"></span>
                                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500 border-2 border-white"></span>
                                </span>
                            )}
                        </button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto overflow-x-hidden thin-scrollbar">
                        {showReleaseNotes ? (
                            <div className="p-4 space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                                {releaseNotesData.map((note, index) => (
                                    <div key={index} className="space-y-4">
                                        <div className={`p-4 rounded-2xl ${colors.bg.secondary} border ${colors.border.primary} shadow-sm space-y-4`}>
                                            {/* <ul className="space-y-3">
                                                {note.content.map((item, i) => (
                                                    <li key={i} className={`text-sm ${colors.text.primary} flex gap-3`}>
                                                        <div className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#13670B] shrink-0" />
                                                        {item}
                                                    </li>
                                                ))}
                                            </ul> */}
                                            <span className={`px-2 py-0.5 rounded-md text-sm font-black ${colors.bg.secondary} border ${colors.border.primary} shadow-sm`}>
                                                {note.version}
                                            </span>
                                            {note.img && (
                                                <div
                                                    className="relative group rounded-xl overflow-hidden border border-gray-100 shadow-inner bg-gray-50 cursor-zoom-in"
                                                    onClick={() => setSelectedImage(note.img)}
                                                >
                                                    <img
                                                        src={note.img}
                                                        alt={`Release ${note.version}`}
                                                        className="w-full object-contain transition-transform duration-300 group-hover:scale-[1.02]"
                                                    />
                                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors flex items-center justify-center">
                                                        <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 p-2 rounded-full shadow-lg">
                                                            <Maximize2 size={20} className="text-gray-700" />
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="p-4 space-y-6">
                                {/* Data Coverage */}
                                <div>
                                    <div className="flex items-center gap-2 mb-3">
                                        <Calendar size={16} className={colors.text.primary} />
                                        <h3 className={`font-semibold ${colors.text.primary}`}>Data Coverage</h3>
                                    </div>
                                    <div className={`rounded-lg p-3 space-y-2`}>
                                        <div className="flex justify-between">
                                            <span className={`text-sm ${colors.text.secondary}`}>Earliest Date:</span>
                                            <span className={`text-sm font-medium ${colors.text.primary}`}>Jan 2023</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className={`text-sm ${colors.text.secondary}`}>Cutoff Date:</span>
                                            <span className={`text-sm font-medium ${colors.text.primary}`}>{cutoffDateLabel}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className={`text-sm ${colors.text.secondary}`}>Daily Refresh:</span>
                                            <span className={`text-sm font-medium ${colors.text.primary}`}>07:30 (GMT+7)</span>
                                        </div>
                                        <div className={`text-xs italic ${colors.text.secondary}`}>
                                            {/* Previous month's data is available after the 20th of each month. */}
                                            Previous month's data is available after the 20th of current month (e.g., February data is available after March 20)                                        </div>
                                    </div>
                                </div>

                                {/* Operating Companies */}
                                <div>
                                    <div className="flex items-center gap-2 mb-3">
                                        <MapPin size={16} className={colors.text.primary} />
                                        <h3 className={`font-semibold ${colors.text.primary}`}>14 OpCos</h3>
                                    </div>
                                    <div className='rounded-lg p-3'>
                                        <div className="grid grid-cols-2 gap-2">
                                            {opcos.map((opco, index) => (
                                                <div key={index} className={`text-sm ${colors.text.secondary}`}>
                                                    {opco}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* KPI Coverage */}
                                <div>
                                    <div className="flex items-center gap-2 mb-3">
                                        <Target size={16} className={colors.text.primary} />
                                        <h3 className={`font-semibold ${colors.text.primary}`}>KPI Coverage</h3>
                                    </div>
                                    <p className={`pl-3 italic text-sm ${colors.text.secondary}`}>All monetary values are calculated in euros (EUR).</p>

                                    <div className='rounded-lg p-3'>
                                        <div className="text-md font-medium mb-2 text-[#13670B]">Distribution Power Pillar</div>
                                        <div className="space-y-1">
                                            {distributionPowerKpis.map((kpi, index) => (
                                                <div key={index} className={`text-sm ${colors.text.secondary}`}>
                                                    • {kpi}
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className='rounded-lg p-3'>
                                        <div className="text-md font-medium mb-2 text-[#13670B]">Transaction Capture Pillar</div>
                                        <div className="space-y-1">
                                            {transactionCaptureKpis.map((kpi, index) => (
                                                <div key={index} className={`text-sm ${colors.text.secondary}`}>
                                                    • {kpi}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className='rounded-lg p-3'>
                                        <div className="text-md font-medium mb-2 text-[#13670B]">Execution Excellence Pillar</div>
                                        <div className="space-y-1">
                                            {executionExcellenceKpis.map((kpi, index) => (
                                                <div key={index} className={`text-sm ${colors.text.secondary}`}>
                                                    • {kpi}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className='rounded-lg p-3'>
                                        <div className="text-md font-medium mb-2 text-[#13670B]">Customer Loyalty Pillar</div>
                                        <div className="space-y-1">
                                            {customerLoyaltyKpis.map((kpi, index) => (
                                                <div key={index} className={`text-sm ${colors.text.secondary}`}>
                                                    • {kpi}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="pt-3 pl-3">For any inquiries, issues, or feedback:</div>
                                    <p className='pl-3 text-md'>
                                        <a
                                            href="mailto:grippyai_apac_support@heiway.onmicrosoft.com"
                                            className="text-blue-600 hover:text-blue-700"
                                        >
                                            grippyai_apac_support@heiway.onmicrosoft.com
                                        </a>
                                    </p>
                                    <div className='p-3'>Last Update: {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {/* Full-screen Image Preview Modal */}
            {selectedImage && (
                <div
                    className="image-preview-modal fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-4 md:p-10 cursor-zoom-out duration-300 pointer-events-auto"
                    onClick={() => setSelectedImage(null)}
                >
                    <div className="relative w-full max-w-7xl h-full flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                        <img
                            src={selectedImage}
                            alt="Full Preview"
                            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl select-none"
                        />
                        <button
                            className="absolute top-0 -right-12 md:top-2 md:right-2 text-white hover:text-gray-300 transition-colors p-2 rounded-full bg-black/20 hover:bg-black/40"
                            onClick={() => setSelectedImage(null)}
                            title="Close preview"
                        >
                            <X size={32} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Guideline;
